import React, { useState } from 'react';
import {
    View,
    Text,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    ActivityIndicator,
    StatusBar,
} from 'react-native';
import { COLORS, API_URL } from '../config';
import { useCustomAlert } from '../components/CustomAlert';

export default function ForgotPasswordScreen({ navigation }) {
    const [step, setStep] = useState(1); // 1 = input telepon, 2 = input password baru
    const [telepon, setTelepon] = useState('');
    const [nama, setNama] = useState('');
    const [passwordBaru, setPasswordBaru] = useState('');
    const [konfirmasiPassword, setKonfirmasiPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { alert } = useCustomAlert();

    // Step 1: Verifikasi nomor telepon
    const handleCheckPhone = async () => {
        if (!telepon) {
            alert('Error', 'Masukkan nomor telepon yang terdaftar');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/reset-password.php?action=check`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ telepon }),
            });
            const data = await response.json();

            if (data.success) {
                setNama(data.data.nama);
                setStep(2);
            } else {
                alert('Tidak Ditemukan', data.message || 'Nomor telepon tidak terdaftar');
            }
        } catch (error) {
            alert('Error', 'Gagal menghubungi server');
        } finally {
            setLoading(false);
        }
    };

    // Step 2: Ganti password
    const handleResetPassword = async () => {
        if (!passwordBaru || !konfirmasiPassword) {
            alert('Error', 'Semua field harus diisi');
            return;
        }

        if (passwordBaru !== konfirmasiPassword) {
            alert('Error', 'Konfirmasi password tidak cocok');
            return;
        }

        if (passwordBaru.length < 6) {
            alert('Error', 'Password minimal 6 karakter');
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/reset-password.php?action=reset`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    telepon,
                    password_baru: passwordBaru,
                    konfirmasi_password: konfirmasiPassword,
                }),
            });
            const data = await response.json();

            if (data.success) {
                alert('Berhasil', data.message, [
                    { text: 'OK', onPress: () => navigation.navigate('Login') }
                ]);
            } else {
                alert('Gagal', data.message || 'Terjadi kesalahan');
            }
        } catch (error) {
            alert('Error', 'Gagal menghubungi server');
        } finally {
            setLoading(false);
        }
    };

    return (
        <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.container}
        >
            <StatusBar barStyle="dark-content" backgroundColor={COLORS.bgDarker} />
            <ScrollView
                contentContainerStyle={styles.scrollContent}
                keyboardShouldPersistTaps="handled"
                showsVerticalScrollIndicator={false}
            >
                {/* Header */}
                <View style={styles.header}>
                    <Text style={styles.emoji}>🔐</Text>
                    <Text style={styles.title}>Lupa Password</Text>
                    <Text style={styles.subtitle}>
                        {step === 1
                            ? 'Masukkan nomor telepon yang terdaftar untuk mereset password'
                            : `Halo ${nama}, silakan buat password baru`}
                    </Text>
                </View>

                {/* Form */}
                <View style={styles.formCard}>
                    {step === 1 ? (
                        // Step 1: Input Telepon
                        <>
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Nomor Telepon</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="08xxxxxxxxxx"
                                    placeholderTextColor={COLORS.textMuted}
                                    keyboardType="phone-pad"
                                    value={telepon}
                                    onChangeText={setTelepon}
                                />
                            </View>

                            <TouchableOpacity
                                style={[styles.button, loading && styles.buttonDisabled]}
                                onPress={handleCheckPhone}
                                disabled={loading}
                            >
                                {loading ? (
                                    <ActivityIndicator color="#fff" />
                                ) : (
                                    <Text style={styles.buttonText}>Lanjut</Text>
                                )}
                            </TouchableOpacity>
                        </>
                    ) : (
                        // Step 2: Input Password Baru
                        <>
                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Password Baru</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="Minimal 6 karakter"
                                    placeholderTextColor={COLORS.textMuted}
                                    secureTextEntry
                                    value={passwordBaru}
                                    onChangeText={setPasswordBaru}
                                />
                            </View>

                            <View style={styles.inputGroup}>
                                <Text style={styles.label}>Konfirmasi Password</Text>
                                <TextInput
                                    style={styles.input}
                                    placeholder="Ulangi password baru"
                                    placeholderTextColor={COLORS.textMuted}
                                    secureTextEntry
                                    value={konfirmasiPassword}
                                    onChangeText={setKonfirmasiPassword}
                                />
                            </View>

                            <TouchableOpacity
                                style={[styles.button, loading && styles.buttonDisabled]}
                                onPress={handleResetPassword}
                                disabled={loading}
                            >
                                {loading ? (
                                    <ActivityIndicator color="#fff" />
                                ) : (
                                    <Text style={styles.buttonText}>Ganti Password</Text>
                                )}
                            </TouchableOpacity>

                            <TouchableOpacity
                                style={styles.backLink}
                                onPress={() => setStep(1)}
                            >
                                <Text style={styles.backLinkText}>← Kembali</Text>
                            </TouchableOpacity>
                        </>
                    )}
                </View>

                {/* Back to Login */}
                <TouchableOpacity
                    style={styles.loginLink}
                    onPress={() => navigation.navigate('Login')}
                >
                    <Text style={styles.loginLinkText}>
                        Sudah ingat password? <Text style={styles.loginLinkBold}>Masuk</Text>
                    </Text>
                </TouchableOpacity>
            </ScrollView>
        </KeyboardAvoidingView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: COLORS.bgDark,
    },
    scrollContent: {
        flexGrow: 1,
        justifyContent: 'center',
        padding: 24,
        paddingTop: 60,
        paddingBottom: 40,
    },
    header: {
        alignItems: 'center',
        marginBottom: 32,
    },
    emoji: {
        fontSize: 48,
        marginBottom: 16,
    },
    title: {
        fontSize: 28,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 8,
    },
    subtitle: {
        fontSize: 14,
        color: COLORS.textSecondary,
        textAlign: 'center',
        paddingHorizontal: 20,
    },
    formCard: {
        backgroundColor: COLORS.bgCard,
        borderRadius: 20,
        padding: 24,
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.1,
        shadowRadius: 12,
        elevation: 5,
    },
    inputGroup: {
        marginBottom: 16,
    },
    label: {
        color: COLORS.textSecondary,
        marginBottom: 8,
        fontWeight: '500',
        fontSize: 14,
    },
    input: {
        backgroundColor: COLORS.bgDark,
        borderRadius: 12,
        padding: 14,
        color: COLORS.textPrimary,
        fontSize: 15,
        borderWidth: 1,
        borderColor: COLORS.border,
    },
    button: {
        backgroundColor: COLORS.primary,
        borderRadius: 12,
        padding: 16,
        alignItems: 'center',
        marginTop: 8,
    },
    buttonDisabled: {
        opacity: 0.7,
    },
    buttonText: {
        color: '#fff',
        fontSize: 16,
        fontWeight: '600',
    },
    backLink: {
        marginTop: 16,
        alignItems: 'center',
    },
    backLinkText: {
        color: COLORS.textSecondary,
        fontSize: 14,
    },
    loginLink: {
        marginTop: 24,
        alignItems: 'center',
    },
    loginLinkText: {
        color: COLORS.textSecondary,
    },
    loginLinkBold: {
        color: COLORS.primary,
        fontWeight: '600',
    },
});
